import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Plus, Trash2, Upload } from 'lucide-react';

export default function CommercializationTabEditor({ data, onChange }) {
  if (!data) return null;

  const updateField = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  const addLink = () => {
    const newLinks = [...(data.links || []), {
      label: '',
      type: 'pdf',
      file: null,
      internal_slug: null,
      note: null
    }];
    updateField('links', newLinks);
  };

  const updateLink = (index, field, value) => {
    const newLinks = [...data.links];
    newLinks[index] = { ...newLinks[index], [field]: value };
    updateField('links', newLinks);
  };

  const removeLink = (index) => {
    const newLinks = data.links.filter((_, i) => i !== index);
    updateField('links', newLinks);
  };

  return (
    <div className="space-y-6 bg-white p-6 rounded-lg border">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Section Title
        </label>
        <Input
          value={data.title || ''}
          onChange={(e) => updateField('title', e.target.value)}
          placeholder="Commercialization of Technologies"
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description (HTML)
        </label>
        <Textarea
          value={data.description || ''}
          onChange={(e) => updateField('description', e.target.value)}
          placeholder="<p>Description...</p>"
          rows={4}
        />
      </div>

      {/* Links */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-gray-700">
            Links & Downloads
          </label>
          <Button onClick={addLink} size="sm" variant="outline">
            <Plus className="w-4 h-4 mr-1" /> Add Link
          </Button>
        </div>
        <div className="space-y-4">
          {data.links && data.links.map((link, index) => (
            <div key={index} className="border rounded p-4 bg-gray-50">
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Label</label>
                  <Input
                    value={link.label}
                    onChange={(e) => updateLink(index, 'label', e.target.value)}
                    placeholder="Link label"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Type</label>
                    <Select
                      value={link.type}
                      onValueChange={(value) => updateLink(index, 'type', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pdf">PDF</SelectItem>
                        <SelectItem value="download">Download</SelectItem>
                        <SelectItem value="internal">Internal Link</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {link.type === 'internal' ? (
                    <div>
                      <label className="text-xs text-gray-600 mb-1 block">Internal Slug</label>
                      <Input
                        value={link.internal_slug || ''}
                        onChange={(e) => updateLink(index, 'internal_slug', e.target.value)}
                        placeholder="/technologies"
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="text-xs text-gray-600 mb-1 block">File Path</label>
                      <Input
                        value={link.file || ''}
                        onChange={(e) => updateLink(index, 'file', e.target.value)}
                        placeholder="/api/files/download/..."
                      />
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">Note (optional)</label>
                  <Input
                    value={link.note || ''}
                    onChange={(e) => updateLink(index, 'note', e.target.value)}
                    placeholder="Additional note"
                  />
                </div>
              </div>
              <Button
                onClick={() => removeLink(index)}
                size="sm"
                variant="destructive"
                className="mt-3"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Remove Link
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
